'use client';

// Import libraries
import { observer } from 'mobx-react-lite';
import Image from 'next/image';
import { useState } from 'react';
import { EnvironmentService } from '@/services/isomorphic/EnvironmentService';
import { msgf } from '@/services/isomorphic/I18NService';
import { type IProduct } from '@/services/models/Product';
import { BadgeTextDisplayPosition } from '@/services/models/Product/BadgeTextDisplayPosition';
import { classes } from '@/next-utils/css-utils/scss-utils';
import { useProductTile } from '@/react/hooks/useProductTile';
import ConfigurationService from '@/services/isomorphic/ConfigurationService';
import { PriceModel } from '@/services/models/Price';
import type { DTO, Nullable } from '@/type-utils';
import { isNullish } from '@/utils/null-utils';
import ProductTileVM, {
  type IAppliedRefinements
} from '@/react/view-models/ProductTileVM';
import type { IImage } from '@/services/models/Media/Image';
import StaleWhileRevalidate from '@/utils/StaleWhileRevalidate';
import { Image as GenericImage } from '../../core-ui/Image';
import { Link } from '../../core-ui/Link';
import RatingStars from '../../core-ui/RatingStars';
import { Price } from '../../product/Price';
import type ITestIdentifiable from '../../traits/ITestIdentifiable';
import { HoverIntent } from '../../utility/HoverIntent';
import ImageSwitcher from '../../utility/ImageSwitcher';
import SkeletonLoader from '../../utility/SkeletonLoader';
import QuickviewButton from '../quickview/QuickviewButton';
import ColorSelector, { ColorOption } from './ColorSelector';
import { ProductSwatchSlider } from './ProductSwatchSlider';
import type { ISwatchVariation } from './ProductSwatchSlider/Swatch';
import { SkipRenderOnServer } from '../../utils/SkipRenderOnServer';
import { Breakpoint, Breakpoints } from '../../core-ui/Breakpoints';
import { BestForText } from './BestForText';

// Import styles and assets

import S from './styles.base.module.scss';
import type IInteractable from '../../traits/IInteractable';
import { product_tiles_numberOfColors } from "@/lang/__generated__/ahnu/product_tiles_numberOfColors";

/** Defines props for the props `ProductTile` component. */
export interface IProductTileProps extends ITestIdentifiable, IInteractable {
  /** The product to display. */
  product: IProduct | DTO<IProduct>;

  /**
   * Provides a badge position to display on Product Tile.
   *
   * @example "BELOW_PRODUCT_IMAGE"
   */
  badgeTextDisplayPosition?: BadgeTextDisplayPosition;

  /** A badge to include on this tile. */
  badge?: string;

  /** Promotional text to include on this tile. */
  promotionText?: string;

  /**
   * Defines tile type.
   * @default "default"
   */
  variant?: 'default' | 'recommendation' | 'cart';

  /**
   * Is recommendation tile in a slide.
   * @default false
   */
  isSlideTile?: boolean;

  /** Show rating stars on this tile. */
  showRatingStars?: boolean;

  /** The applied refinements by the user. */
  appliedRefinements?: IAppliedRefinements;
}

const DEFAULT_APPLIED_REFINEMENTS: IAppliedRefinements = {
  color: false,
  size: false
};

/** Component that displays a product in a results or category page.  */
const ProductTile = observer(function ProductTile({
  product,
  badgeTextDisplayPosition,
  badge,
  promotionText,
  variant = 'default',
  isSlideTile,
  datatestID,
  onClick,
  interactionDetails,
  showRatingStars = true,
  appliedRefinements = DEFAULT_APPLIED_REFINEMENTS
}: IProductTileProps) {
  // Wheter the mouse is over the component. If so, it will display the product rating.
  const [isMouseOver, setIsMouseOver] = useState(false);
  const isRecommendationVariant = variant === 'recommendation';
  const isCartVariant = variant === 'cart';

  const productConfig = ConfigurationService.getConfig('product');

  const shouldUseQuickColorSelector = productConfig.getSetting(
    'plp.useQuickColorSelector'
  ).value;
  const isQuickViewEnabled = productConfig.getSetting('quickView.show').value;
  const mobileSwatchesPerView = productConfig.getSetting(
    'plp.mobileSwatchesPerView'
  ).value;
  const showGenderOnProductTiles = productConfig.getSetting(
    'plp.showGenderOnProductTiles'
  ).value;

  /**
   * Define whether hover intent is enabled or not.
   * @returns Boolean.
   */
  function isHoverIntentEnabled(): boolean {
    if (isCartVariant) {
      // Always disable cart variant hover interactions.
      return false;
    }
    if (isRecommendationVariant) {
      // If Recommendations Variant, check config.
      const recommendationsHoverIntentEnabled = productConfig.getSetting(
        'recommendations.hoverIntentEnabled'
      ).value;
      return recommendationsHoverIntentEnabled;
    }
    // default to true;
    return true;
  }
  const hoverIntentEnabled = isHoverIntentEnabled();

  const backgroundColor = productConfig.getSetting('background.tile').value;

  // Check if the "Best For" shoe surface text should be shown.
  const showBestForText = productConfig.getSetting('plp.showBestForText').value;

  // Check if the "Percentage Off Text (i.e. 40% OFF)" should show.
  const showPercentageOffText = productConfig.getSetting(
    'plp.showPercentageOffText'
  ).value;

  // Get strikethrough position for PLP from product config
  const strikethroughPosition = productConfig.getSetting(
    'pricing.strikethroughPosition.plp'
  ).value;

  // Check if the color variants counter should be shown.
  // const showColorVariantsCounter = pdpConfig.getSetting(
  //   'colorVariantsCounter.show'
  // ).value;

  const productTileViewModel = useProductTile(product, appliedRefinements);

  const { productModel, colorVariants, groupDisplayName } =
    productTileViewModel;

  const { name, price } = productModel;

  const primaryImage = productModel?.primaryImage ?? null;
  const secondaryImage = productModel?.secondaryImage ?? primaryImage;

  const [currentImage, setCurrentImage] = useState<Nullable<IImage>>(
    primaryImage ?? null
  );

  const { origin } = EnvironmentService.url;

  const priceModel = price ? PriceModel.from(price) : null;

  // Create swatches for each color variant.
  const swatches =
    colorVariants.map((color) => {
      const [sku] = productModel.selectAttributeSKU(color);
      return { sku, ...color };
    }) ?? [];

  // Retrieve paths to send to product page from a category page.
  const href = productModel.getUrlWithCategory();

  /**
   * Check if Tile Color Selector should be enabled or not.
   * @returns Boolean.
   */
  function isColorSelectorEnabled(): boolean {
    // Always disable color selector if is cart variant or if there are no color variants;
    if (isCartVariant || colorVariants.length === 0) {
      return false;
    }
    if (isRecommendationVariant) {
      // If Recommendations Variant, check config;
      const recommendationsColorSelectorEnabled = productConfig.getSetting(
        'recommendations.colorSelectorEnabled'
      ).value;
      return recommendationsColorSelectorEnabled;
    }
    // Default to true;
    return true;
  }
  const colorSelectorEnabled = isColorSelectorEnabled();

  const onSwatchChange = async ([swatch, previousSwatch]: [
    ISwatchVariation,
    Nullable<ISwatchVariation>
  ]): Promise<void> => {
    if (swatch.sku === previousSwatch?.sku) return;

    if (swatch.secondaryImage) {
      setCurrentImage(swatch.secondaryImage);
    }

    await productTileViewModel.selectProductVariantBySKU(swatch.sku);
  };

  const badgeTextDisplayPositionConfig = ConfigurationService.getConfig(
    'productList'
  ).getSetting('badges.plp.badgeTextPosition').value;

  const badgeTextPosition =
    badgeTextDisplayPosition ?? badgeTextDisplayPositionConfig;
  const productRatings =
    productTileViewModel.ratings instanceof StaleWhileRevalidate
      ? (productTileViewModel.ratings.value?.numberOfRatings ?? 0)
      : productTileViewModel.ratings.numberOfRatings;
  /**
   * Display the rating stars if the property `showRatingStars` is set to true, the user is not in the cart
   * page, the mouse is over this product and the product itself has at least one review.
   */
  const displayRatingStars =
    showRatingStars && !isCartVariant && isMouseOver && productRatings > 0;

  // "Hide" the tile entirely if it doesn't have any stock. __This is the current behavior in
  // the live site__.
  if (!productTileViewModel.productHasAnyStock) return null;

  return (
    <HoverIntent
      delay={0}
      onHoverIn={() => {
        if (hoverIntentEnabled) {
          setCurrentImage(secondaryImage);
          setIsMouseOver(true);
        }
      }}
      onHoverOut={() => {
        if (hoverIntentEnabled) {
          setCurrentImage(primaryImage);
          setIsMouseOver(false);
        }
      }}
    >
      <div
        className={classes(
          !isCartVariant && S.productTile,
          isRecommendationVariant && S.recommendation,
          isSlideTile && S.slideTile,
          isCartVariant && S.cart,
          !shouldUseQuickColorSelector && S.withProductSwatchSlider
        )}
        data-testid={datatestID}
      >
        <div
          className={classes(
            S.content,
            !shouldUseQuickColorSelector && S.withProductSwatchSlider,
            isMouseOver && S.active
          )}
        >
          {badge != null ? (
            <div className={S.badge}>
              <Image
                // Temporary badge id as alt tag.
                alt={badge}
                className={S.image}
                src={badge}
                fill
              />
            </div>
          ) : null}

          {isQuickViewEnabled && !isCartVariant && (
            <div className={S.quickShopButton}>
              <QuickviewButton product={productModel} />
            </div>
          )}

          <div className={S.productImageContainer}>
            {product.badgeImage !== undefined && (
              <div className={S.badgeContainer}>
                <Breakpoints>
                  <Breakpoint default>
                    <Image
                      src={product.badgeImage}
                      alt="badge"
                      width={60}
                      height={60}
                    />
                  </Breakpoint>

                  <Breakpoint media="phone">
                    <Image
                      src={product.badgeImage}
                      alt="badge"
                      width={36}
                      height={36}
                    />
                  </Breakpoint>
                </Breakpoints>
              </div>
            )}

            {!isCartVariant ? (
              <Link
                href={href}
                variant="text"
                onClick={onClick}
                interactionDetails={interactionDetails}
              >
                <div className={S.imageContainer} style={{ backgroundColor }}>
                  {/* check for bad data */}
                  {!isNullish(currentImage) && (
                    <ImageSwitcher
                      image={currentImage}
                      imageProps={{ fit: 'contain' }}
                      className={S.imageSwitcher}
                      imageClassName={classes(
                        S.imageSwitcherInner,
                        isRecommendationVariant && S.slideTileImage
                      )}
                    />
                  )}
                </div>
              </Link>
            ) : (
              <Link
                href={href}
                variant="text"
                onClick={onClick}
                interactionDetails={interactionDetails}
              >
                <div className={S.imageContainer}>
                  <GenericImage image={primaryImage} />
                </div>
              </Link>
            )}
          </div>

          <div className={S.productTileDetails}>
            {colorSelectorEnabled && !shouldUseQuickColorSelector && (
              <div className={S.productSwatchRow}>
                <Breakpoints>
                  <Breakpoint default>
                    <ProductSwatchSlider
                      swatches={swatches}
                      swatchesPerSlide={4}
                      onChange={onSwatchChange}
                    />
                  </Breakpoint>

                  <Breakpoint media="phone">
                    <ProductSwatchSlider
                      swatches={swatches}
                      swatchesPerSlide={mobileSwatchesPerView}
                      onChange={onSwatchChange}
                    />
                  </Breakpoint>
                </Breakpoints>
              </div>
            )}

            <div className={S.badgeTextContainer}>
              {!isNullish(product.badgeText) &&
                badgeTextPosition === 'BELOW_PRODUCT_IMAGE' && (
                  <span className={S.badgeText}>{product.badgeText}</span>
                )}

              <Link
                href={href}
                variant="text"
                datatestID="plpProductName"
                onClick={onClick}
                interactionDetails={interactionDetails}
              >
                {showGenderOnProductTiles && (
                  <span className={S.productGender}>{groupDisplayName} </span>
                )}
                <span className={S.productName}>{name}</span>
              </Link>
            </div>

            <div className={S.infoRow}>
              {priceModel && (
                <Price
                  price={priceModel}
                  className={S.currentPrice}
                  showDiscountText={showPercentageOffText}
                  strikethroughPosition={strikethroughPosition}
                  variant="adjacent"
                />
              )}

              {/* Colors/variants */}
              {colorVariants.length > 0 && (
                <div className={S.colorVariantsCountWrapper}>
                  <div className={S.colorVariantsCountDivider}>/</div>
                  <div className={S.colorVariantsCount}>
                    {msgf(product_tiles_numberOfColors, {
                      itemCount: colorVariants.length
                    })}
                  </div>
                </div>
              )}
            </div>

            {showBestForText && !isCartVariant && (
              <div className={S.infoRow}>
                <BestForText shoeSurface={productModel.shoeSurface} />
              </div>
            )}

            {colorSelectorEnabled && shouldUseQuickColorSelector ? (
              <div className={S.colors}>
                <ColorSelector>
                  {/* TODO: Use actual variant colors here. */}
                  {colorVariants.map((color) => {
                    const [newSKU] = productModel.selectAttributeSKU(color);
                    const url = new URL(`/p/${newSKU}`, origin);

                    return (
                      <ColorOption
                        key={newSKU}
                        color={color}
                        onHover={() => {
                          if (color.secondaryImage) {
                            setCurrentImage(color.secondaryImage);
                          }
                        }}
                        href={url}
                      />
                    );
                  })}
                </ColorSelector>
              </div>
            ) : null}

            {!isNullish(promotionText) && (
              <div className={S.promotionText}>{promotionText}</div>
            )}
          </div>
        </div>
        {
          /**
           * This extra content section is absolutely positioned to appear over any tile or element
           * within a grid or list of product tiles. It becomes visible on hover to show additional
           * product details, such as ratings.
           *
           * _Note_: Ideally any dynamic content on hover should be here to avoid layout shifts. If we need
           * to add more hovered content in the future that doesn't appear below the tile, we should consider
           * refactoring the entire tile structure to better accommodate dynamic content.
           */
          isMouseOver && (
            <div className={S.extraContent}>
              {displayRatingStars && (
                <ProductTileRatingStars
                  ratings={productTileViewModel.ratings}
                />
              )}
            </div>
          )
        }
      </div>
    </HoverIntent>
  );
});

const ProductTileRatingStars = observer(function ProductTileRatingStars({
  ratings
}: {
  ratings: ProductTileVM['ratings'];
}) {
  const [loading, averageRating] =
    ratings instanceof StaleWhileRevalidate
      ? [ratings.pending, ratings.value?.averageRating]
      : [false, ratings.averageRating];
  return (
    <div className={S.ratings}>
      {/* Skip rendering until after hydration to avoid hydration errors. */}
      <SkipRenderOnServer
        fallback={<SkeletonLoader className={S.skeletonLoader} />}
      >
        {!loading ? (
          <RatingStars rating={averageRating ?? 0} />
        ) : (
          // A skeleton loading indicator would go well here.
          <SkeletonLoader className={S.skeletonLoader} />
        )}
      </SkipRenderOnServer>
    </div>
  );
});

export default ProductTile;
